import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIPlugin } from '../../../aiPluginType';
import { aiExperienceDecorationPluginKey } from '../../../pm-plugins/decoration/decoration-plugin-key';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import type { StepName } from '../../utils/analytics/analyticsFlowTypes';
import { useAnalyticsFlow } from '../../utils/analytics/analyticsFlowUtils';
import { rangeCharWordCount } from '../../utils/analytics/utils';
import { getSelectionCategory, getSelectionNodeTypes } from '../../utils/selection';

/**
 * Ref type for bridging the React-based AnalyticsFlow to the pure-pm plugin.
 * The ref is held in the plugin factory closure (aiPlugin) and set by
 * AnalyticsBridge via api.aiExperience.actions.setAnalyticsBridgeRef.
 * The pure-pm plugin receives a getter and calls it when firing steps.
 */
export type AnalyticsFlowBridgeRef = {
	current: {
		fireAnalyticsStep: (step: StepName, targetStep?: StepName) => void;
	} | null;
};

/**
 * Bridges analytics data from ProseMirror plugin state into the userFlow
 * analytics flow.
 *
 * On the pure-pm path, streaming operations happen in the ProseMirror plugin
 * without React involvement. This component sits between
 * AnalyticsFlowContextProvider and RenderScreen, giving it a stable lifecycle
 * that spans all screen transitions. This allows it to:
 *
 * - Accumulate state across screen changes (e.g., durations from loading
 *   through to accept/reject)
 * - Observe ProseMirror state changes continuously
 * - Sync data from the plugin world into the analytics flow
 */
export function AnalyticsBridge({
	api,
	children,
	uiState,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	children: React.ReactNode;
	uiState: string;
}) {
	const analyticsFlow = useAnalyticsFlow();
	const lastQueuedStep = useRef<StepName | undefined>();

	const { editorView, canInlineStreamChanges } = useContentComponentContext();

	// Read decoration state synchronously so that common attributes are
	// available before the first analytics step fires. The hook-based values
	// serve as a reactive fallback for later updates.
	const decorationState = editorView?.state
		? aiExperienceDecorationPluginKey.getState(editorView.state)
		: undefined;

	const {
		traceId,
		configItem: hookConfigItem,
		initialPositions: hookInitialPositions,
	} = useSharedPluginStateWithSelector(api, ['aiExperience'], (state) => ({
		traceId: state.aiExperienceState?.aiDecorationExperiencePluginState.traceId,
		configItem: state.aiExperienceState?.aiDecorationExperiencePluginState.configItem,
		initialPositions: state.aiExperienceState?.aiDecorationExperiencePluginState.initialPositions,
	}));

	const configItem = decorationState?.configItem ?? hookConfigItem;
	const initialPositions = decorationState?.initialPositions ?? hookInitialPositions;

	const isStreaming = useSharedPluginStateWithSelector(api, ['aiExperience'], (state) =>
		Boolean(state.aiExperienceState?.isStreaming),
	);

	const isIdle = useRef(true);

	// Track time spent in loading vs streaming for duration measurements.
	// These persist across screen transitions since this component doesn't unmount.
	const loadingStartTime = useRef(performance.now());
	const streamingStartTime = useRef<number>();

	if (!streamingStartTime.current && isStreaming) {
		streamingStartTime.current = performance.now();
	}

	useEffect(() => {
		if (uiState === 'idle' && !isIdle.current) {
			loadingStartTime.current = performance.now();
			isIdle.current = true;
		} else {
			isIdle.current = false;
		}
	}, [uiState]);

	useEffect(() => {
		const streamingStart = streamingStartTime.current;
		const loadingStart = loadingStartTime.current;

		return () => {
			if (!streamingStart) {
				return;
			}

			analyticsFlow.addAttributes({
				durations: {
					timeSpentWithoutPartialPreview: streamingStart - loadingStart,
					timeSpentWithPartialPreview: performance.now() - streamingStart,
				},
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Sync traceId into common attributes when it becomes available from the backend
	useEffect(() => {
		if (traceId) {
			analyticsFlow.updateCommonAttributes({
				traceIds: [traceId],
			});
		}
	}, [traceId, analyticsFlow]);

	// Populate common analytics attributes
	useEffect(() => {
		if (!configItem) {
			return;
		}

		const backendModel = configItem.getBackendModel?.();
		const autoAcceptAIChanges = canInlineStreamChanges();
		const isInvokedFromEmptyPage = isEmptyDocument(editorView.state.doc);

		analyticsFlow.updateCommonAttributes({
			backendModel,
			...analyticsFlow.getUserFlowCommonAttributes(configItem),
			autoAcceptAIChanges,
			inlineStreamingAvailable: true,
			isInvokedFromEmptyPage,
		});

		if (initialPositions) {
			const selectionCategory = getSelectionCategory(editorView, initialPositions);
			const { charCount, wordCount } = rangeCharWordCount(
				editorView.state.doc,
				initialPositions[0],
				initialPositions[1],
			);

			analyticsFlow.updateCommonAttributes({
				selectionCharCount: charCount,
				selectionWordCount: wordCount,
				selectionCategory,
			});

			if (fg('platform_editor_ai_aifc_patch_ga_blockers')) {
				const selectionNodes = getSelectionNodeTypes(editorView.state, initialPositions);
				analyticsFlow.updateCommonAttributes({ selectionNodes });
			}
		}
	}, [analyticsFlow, configItem, initialPositions, editorView, canInlineStreamChanges]);

	// Defer analytics firing to a useEffect so that synchronous calls from the
	// ProseMirror plugin don't trigger the event mid-render.
	const [pendingStep, setPendingStep] = useState<{
		step: StepName;
		targetStep?: StepName;
	} | null>(null);

	const fireAnalyticsStep = useCallback((step: StepName, targetStep?: StepName) => {
		if (lastQueuedStep.current !== step) {
			lastQueuedStep.current = step;
			setPendingStep({ step, targetStep });
		}
	}, []);

	useEffect(() => {
		if (!pendingStep) {
			return;
		}

		const { step, targetStep } = pendingStep;

		// invoke is already queued on reset
		if (step !== 'invoke') {
			analyticsFlow.enqueueStep({ stepName: step });
		}

		analyticsFlow.fireQueuedStep(targetStep);

		setPendingStep(null);
	}, [pendingStep, analyticsFlow]);

	// Expose fireAnalyticsStep to the pure-pm plugin via the plugin API (ref so plugin always calls current callback)
	const bridgeRef = React.useRef<AnalyticsFlowBridgeRef['current']>(null);
	useEffect(() => {
		bridgeRef.current = { fireAnalyticsStep };
		api?.aiExperience?.actions?.setAnalyticsBridgeRef?.(bridgeRef);
		return () => {
			api?.aiExperience?.actions?.setAnalyticsBridgeRef?.(null);
			bridgeRef.current = null;
		};
	}, [api, fireAnalyticsStep]);

	return <>{children}</>;
}
